import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ScorecardCategory, ScorecardCategoryInput } from "@/types/scorecard";

const PRESET_COLORS = [
	"#3B82F6", // blue
	"#8B5CF6", // violet
	"#EC4899", // pink
	"#EF4444", // red
	"#F59E0B", // amber
	"#10B981", // emerald
	"#06B6D4", // cyan
	"#6B7280", // gray
];

interface CategoryFormDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Category being edited, or null when creating. */
	category: ScorecardCategory | null;
	onSubmit: (input: ScorecardCategoryInput) => Promise<void>;
	saving: boolean;
}

/** Create/edit dialog for a scorecard category (name, description, color). */
export const CategoryFormDialog: React.FC<CategoryFormDialogProps> = ({
	open,
	onOpenChange,
	category,
	onSubmit,
	saving,
}) => {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [color, setColor] = useState(PRESET_COLORS[0]);

	useEffect(() => {
		if (!open) return;
		setName(category?.name ?? "");
		setDescription(category?.description ?? "");
		setColor(category?.color ?? PRESET_COLORS[0]);
	}, [open, category]);

	const handleSubmit = async () => {
		if (!name.trim()) return;
		await onSubmit({
			id: category?.id,
			name: name.trim(),
			description: description.trim() || null,
			color,
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{category
							? t("admin.scorecardFields.categories.edit", "Edit category")
							: t("admin.scorecardFields.categories.add", "Add category")}
					</DialogTitle>
					<DialogDescription>
						{t(
							"admin.scorecardFields.categories.formDescription",
							"Categories group scorecard criteria into sections on the candidate scorecard.",
						)}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="scorecard-category-name">
							{t("admin.scorecardFields.categories.name", "Name")}
						</Label>
						<Input
							id="scorecard-category-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder={t(
								"admin.scorecardFields.categories.namePlaceholder",
								"e.g. Technical skills",
							)}
							autoFocus
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="scorecard-category-description">
							{t("admin.scorecardFields.categories.description", "Description (optional)")}
						</Label>
						<Input
							id="scorecard-category-description"
							value={description}
							onChange={(event) => setDescription(event.target.value)}
							placeholder={t(
								"admin.scorecardFields.categories.descriptionPlaceholder",
								"Shown under the section title",
							)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>{t("admin.scorecardFields.categories.color", "Color")}</Label>
						<div className="flex items-center gap-2">
							{PRESET_COLORS.map((preset) => (
								<button
									key={preset}
									type="button"
									aria-label={preset}
									aria-pressed={color === preset}
									className={cn(
										"h-6 w-6 rounded-full border border-border transition-transform focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
										color === preset && "scale-110 ring-2 ring-ring ring-offset-2 ring-offset-background",
									)}
									style={{ backgroundColor: preset }}
									onClick={() => setColor(preset)}
								/>
							))}
							<input
								type="color"
								aria-label={t("admin.scorecardFields.categories.customColor", "Custom color")}
								value={color}
								onChange={(event) => setColor(event.target.value)}
								className="h-6 w-8 cursor-pointer rounded-sm border border-border bg-transparent p-0.5"
							/>
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!name.trim() || saving}>
						{saving
							? t("common.saving", "Saving...")
							: t("common.save", "Save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
